import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Select, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";

import { all_routes } from "../../../../../router/all_routes";
import CollapseHeader from "../../../../../../core/common/collapse-header/collapse-header";
import {
  getAllComapniesList,
  getAllEmployeesListDataByCompanyId,
  getLeaveTypes,
  listBalanceAdjustments,
} from "../../../../../../services/attendanceService";
import { getErrorMessage } from "../../../../../../core/data/redux/utils/errorUtils";
import { notify } from "../../../../../../services/toast.config";
import CommonSelect from "../../../../../../core/common/commonSelect";

import AntdDataTable from "../../settings/tabs/components/AntdDataTable";

import type {
  AdjustmentRow,
  EmployeeOption,
  LeaveTypeOption,
  Pagination,
} from "../../settings/tabs/types";

import "../../settings/styles/leaveSettings.css";

type CompanyOption = {
  value: string;
  label: string;
  type: string;
};

const currentYear = new Date().getFullYear();

const DEFAULT_PAGINATION: Pagination = {
  total: 0,
  current_page: 1,
  per_page: 20,
  total_pages: 0,
};

const AdjustmentLogPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  );
  const [employees, setEmployees] = useState<EmployeeOption[]>([
    { value: "", label: "All Employees" },
  ]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeOption[]>([]);

  const [tempUserId, setTempUserId] = useState<string>(
    searchParams.get("user_id") || "",
  );
  const [tempLeaveTypeId, setTempLeaveTypeId] = useState<number | null>(
    searchParams.get("leave_type_id")
      ? parseInt(searchParams.get("leave_type_id") as string, 10)
      : null,
  );
  const [tempYear, setTempYear] = useState<number>(
    searchParams.get("year")
      ? parseInt(searchParams.get("year") as string, 10)
      : currentYear,
  );

  const [filters, setFilters] = useState<{
    userId: string;
    leaveTypeId: number | null;
    year: number;
  }>({
    userId: tempUserId,
    leaveTypeId: tempLeaveTypeId,
    year: tempYear,
  });

  const [rows, setRows] = useState<AdjustmentRow[]>([]);
  const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);

  const yearOptions = useMemo(
    () =>
      [currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(
        (y) => ({ value: y, label: String(y) }),
      ),
    [],
  );

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (companies.length > 0 && !selectedCompany) {
      setSelectedCompany(companies[0]);
    }
  }, [companies]);

  useEffect(() => {
    if (!selectedCompany?.value) return;
    fetchEmployees();
    fetchCompanyLeaveTypes();
  }, [selectedCompany?.value]);

  useEffect(() => {
    if (!selectedCompany?.value) return;
    fetchPage(1, pagination.per_page, filters);
  }, [selectedCompany?.value, filters]);

  const fetchCompanies = async () => {
    try {
      const res = await getAllComapniesList();
      const list: CompanyOption[] = res.data.data.data.map((c: any) => ({
        value: c.id.toString(),
        label: `${c.name} - ${c.type}`,
        type: c.type,
      }));
      setCompanies(list);
    } catch (err) {
      notify.error("Failed to load companies");
    }
  };

  const fetchEmployees = async () => {
    if (!selectedCompany) return;
    try {
      const res = await getAllEmployeesListDataByCompanyId({
        company_id: selectedCompany.value,
        company_type: selectedCompany.type,
      });
      const fetched = res?.data?.data?.allUsers || [];
      setEmployees([
        { value: "", label: "All Employees" },
        ...fetched.map((e: any) => ({
          value: e.id.toString(),
          label: `${e.salutation || ""} ${e.fname} ${e.lname}`.trim(),
        })),
      ]);
    } catch {
      setEmployees([{ value: "", label: "All Employees" }]);
    }
  };

  const fetchCompanyLeaveTypes = async () => {
    if (!selectedCompany) return;
    try {
      const res = await getLeaveTypes(selectedCompany);
      const list: any[] = res?.data?.data?.leaveTypes || [];
      setLeaveTypes(
        list
          .filter((lt) => lt.balance !== -1)
          .map((lt) => ({
            id: lt.id,
            name: lt.name,
            balance: lt.balance,
            isMonthlyAccrual: lt.isMonthlyAccrual,
            monthlyCarryForward: lt.monthlyCarryForward,
          })),
      );
    } catch {
      setLeaveTypes([]);
    }
  };

  const fetchPage = async (
    page: number,
    perPage: number,
    f: { userId: string; leaveTypeId: number | null; year: number },
  ) => {
    setLoading(true);
    try {
      const payload: Record<string, any> = { page, per_page: perPage };
      if (f.userId) payload.user_id = parseInt(f.userId, 10);
      if (f.leaveTypeId) payload.leave_type_id = f.leaveTypeId;
      if (f.year) payload.year = f.year;
      const res = await listBalanceAdjustments(payload);
      const body = res?.data?.data || {};
      setRows(body.data || []);
      setPagination(body.pagination || DEFAULT_PAGINATION);
    } catch (err) {
      notify.error(getErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    setFilters({
      userId: tempUserId,
      leaveTypeId: tempLeaveTypeId,
      year: tempYear,
    });
  };

  const resetFilters = () => {
    setTempUserId("");
    setTempLeaveTypeId(null);
    setTempYear(currentYear);
    setFilters({ userId: "", leaveTypeId: null, year: currentYear });
  };

  const columns: ColumnsType<AdjustmentRow> = [
    {
      title: "When",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (v: string) => moment(v).format("DD MMM YY HH:mm"),
    },
    {
      title: "Employee",
      dataIndex: "userName",
      key: "userName",
      render: (v: string | null, record) => v || `User #${record.userId}`,
    },
    {
      title: "Type",
      dataIndex: "leaveTypeName",
      key: "leaveTypeName",
      render: (v: string | null, record) =>
        v || `Type #${record.leaveTypeId}`,
    },
    {
      title: "Month",
      dataIndex: "monthKey",
      key: "monthKey",
      width: 110,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Delta",
      dataIndex: "delta",
      key: "delta",
      width: 100,
      render: (v: number) => (
        <Tag color={v > 0 ? "green" : "red"}>{v > 0 ? `+${v}` : v}</Tag>
      ),
    },
    {
      title: "Mode",
      dataIndex: "appliedTo",
      key: "appliedTo",
      width: 150,
      render: (v: string) => (
        <Tag color={v === "balance_only" ? "blue" : "purple"}>{v}</Tag>
      ),
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      ellipsis: true,
    },
    {
      title: "By",
      dataIndex: "performedByUserName",
      key: "performedByUserName",
      render: (v: string | null, record) =>
        v || `User #${record.performedByUserId}`,
    },
  ];

  return (
    <div className="page-wrapper leave-settings">
      <div className="content">
        <div className="ls-header card border-0 mb-3">
          <div className="ls-header__inner d-md-flex d-block align-items-center justify-content-between">
            <div className="my-auto mb-3 mb-md-0">
              <h2 className="mb-1 d-flex align-items-center gap-2">
                <span className="ls-title-dot" />
                Balance Adjustment Audit Log
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.leavesettings}>Leave Settings</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Audit Log
                  </li>
                </ol>
              </nav>
            </div>

            <div className="d-flex right-content align-items-center flex-wrap gap-2">
              <CommonSelect
                options={companies}
                defaultValue={selectedCompany || undefined}
                onChange={(opt: any) => setSelectedCompany(opt)}
                className="select-filter"
              />
              <Link
                to={`${all_routes.leavesettings}?tab=balance`}
                className="btn btn-outline-secondary"
              >
                <i className="ti ti-arrow-back-up me-1" />
                Back to Settings
              </Link>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body bg-white">
            <div className="d-flex flex-wrap gap-3 align-items-end">
              <div style={{ minWidth: 240, flex: 1 }}>
                <div className="text-muted small mb-1">Employee</div>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={tempUserId || undefined}
                  onChange={(v) => setTempUserId(v || "")}
                  options={employees.filter((e) => e.value !== "")}
                  placeholder="All employees"
                  style={{ width: "100%" }}
                />
              </div>
              <div style={{ minWidth: 220 }}>
                <div className="text-muted small mb-1">Leave Type</div>
                <Select
                  allowClear
                  value={tempLeaveTypeId || undefined}
                  onChange={(v) => setTempLeaveTypeId(v ?? null)}
                  options={leaveTypes.map((lt) => ({
                    value: lt.id,
                    label: lt.name,
                  }))}
                  placeholder="All types"
                  style={{ width: "100%" }}
                />
              </div>
              <div style={{ minWidth: 120 }}>
                <div className="text-muted small mb-1">Year</div>
                <Select
                  value={tempYear}
                  onChange={(v) => setTempYear(v)}
                  options={yearOptions}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="d-flex gap-2 ms-auto">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={resetFilters}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={applyFilters}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body bg-white p-0 mb-3">
            <AntdDataTable<AdjustmentRow>
              columns={columns}
              dataSource={rows}
              rowKey="id"
              loading={loading}
              pagination={pagination}
              onPageChange={(page, pageSize) =>
                fetchPage(page, pageSize, filters)
              }
              scrollX={1100}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdjustmentLogPage;
